import React from 'react';
import { useTranslation } from 'react-i18next';
import { WeezHeader, Button, Icon, Tab } from '@weezevent/nacre';
import { useLocation, useHistory } from 'react-router-dom';
import ProductModel from '../../../models/productModel';
import ProductsTable from './components/productsTable';
import CreateProductModal from './components/createProductModal';
import { useFilters, usePageSize, useCurrentPage } from '../../manager/components/context';
import { getProductsLanguages, getDefaultLanguage } from '../utils/translationUtils';
import { convertMenhirToPyvarPipeTranslate } from '../../../components/utils/pipeTranslateConvert';

const spec = {
    multiple: [],
    fields: ['search', 'ordering', 'for_buyer', 'disabled', 'archived'],
    aliases: {}
};

export default function ProductList({ organization }) {
    const { t } = useTranslation();
    const location = useLocation();
    const history = useHistory();
    const [openCreateModal, setOpenCreateModal] = React.useState(false);
    const [products, setProducts] = React.useState([]);
    const [filters, setFilters] = useFilters(spec);
    const [pageSize] = usePageSize(50);
    const [currentPage, setCurrentPage] = useCurrentPage(0);
    const productLanguages = React.useMemo(() => getProductsLanguages(organization), [organization]);
    const defaultLanguage = React.useMemo(() => getDefaultLanguage(organization, productLanguages), [organization, productLanguages]);

    const queryParams = new URLSearchParams(location.search);
    const defaultCategory = parseInt(queryParams.get('category')) || 2;

    const [tab, setTab] = React.useState(defaultCategory);

    const panes = [
        { menuItem: t('ticket.cross-selling.product'), menuIcon: 'shirt', value: 2 },
        { menuItem: t('ticket.cross-selling.service'), menuIcon: 'fee', value: 3 },
        { menuItem: t('ticket.cross-selling.cashless'), menuIcon: 'credit-card', value: 1 }
    ];

    const handleTabChange = tabValue => {
        setTab(tabValue);

        const currentQueryParams = new URLSearchParams(location.search);
        currentQueryParams.set('category', tabValue);

        history.push({
            search: currentQueryParams.toString()
        });
    };

    let handleSearchQuery = filters => {
        let query = {};

        if (filters.search !== '') {
            query.search = filters.search;
        }
        if (filters.disabled) {
            if (filters.disabled === 'Archived') {
                query.archived = true;
            } else {
                query.disabled = filters.disabled === 'True';
            }
        }
        if (filters.for_buyer) {
            query.for_buyer = filters.for_buyer === 'True';
        }

        return query;
    };

    const getPipeTranslate = React.useCallback(
        string => {
            let translations = {};

            if (string.includes('|')) {
                string.split('|').forEach(item => {
                    let pipe = convertMenhirToPyvarPipeTranslate(item);
                    let regLang = pipe.slice(1, 6).toLowerCase();
                    pipe = pipe.slice(7, pipe.length);
                    if (productLanguages?.find(l => regLang === l)) translations[regLang] = pipe;
                });
            }
            return translations;
        },
        [productLanguages]
    );

    const initTranslations = React.useCallback(
        (obj, lang, fields) => {
            if (!obj.translations) obj.translations = {};
            fields?.forEach(field => {
                const pipeTranslate = getPipeTranslate(obj[field]);
                obj.translations[field] = { ...pipeTranslate, ...obj.translations[field] };
                //use name if no translations for default language
                if (obj.translations[field][lang]) obj[field] = obj.translations[field][lang];
                if (!obj.translations[field][lang]) obj.translations[field][lang] = obj[field];
                //we filter the languages keys that arent in organization
                Object.keys(obj.translations[field]).forEach(langKey => {
                    if (!productLanguages.some(ol => ol === langKey)) obj.translations[field][langKey] = '';
                });
            });
            return obj;
        },
        [productLanguages]
    );

    const [, loading, sync, , totalCount] = ProductModel.useApiModel(
        {
            organizationId: organization.id,
            cache: false,
            query: {
                limit: pageSize,
                offset: currentPage * pageSize,
                ordering: 'name',
                expand: ['variants'],
                category: tab,
                ...handleSearchQuery(filters)
            },
            onSuccess: response => {
                let objs = response;
                //we init the translations in case we don't have any
                objs = objs.map(product => {
                    product.object.variants = product.object.variants?.map(variant => {
                        return initTranslations(variant, defaultLanguage, ['name']);
                    });
                    return product;
                });
                setProducts(objs);
            }
        },
        [organization, spec, filters]
    );

    return (
        <>
            <CreateProductModal organization={organization} openModal={openCreateModal} setOpenModal={setOpenCreateModal} sync={sync} />
            <WeezHeader
                title={t('ticket.cross-selling.title')}
                rightComponent={[
                    <Button key={0} primary icon={<Icon name="plus" />} onClick={() => setOpenCreateModal(true)}>
                        {t('ticket.common.cta.add')}
                    </Button>
                ]}
            >
                {t('ticket.cross-selling.listProducts.headerSub')}
            </WeezHeader>
            <Tab panes={panes} activeTab={panes.findIndex(p => p.value === tab)} onTabSelected={selectedTab => handleTabChange(selectedTab.value)} />
            <ProductsTable
                organization={organization}
                products={products}
                loading={loading}
                sync={sync}
                filters={filters}
                setFilters={setFilters}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                pageSize={pageSize}
                totalCount={totalCount}
            />
        </>
    );
}
